import { useState, useRef, useEffect } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, getAnimation, buildSrcSet, getImageSizes } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import gsap from 'gsap';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

export default function GalleryTabsSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const categories = content.categories || [];
    const anim = getAnimation(config, visible);
    const [activeTab, setActiveTab] = useState(0);
    const [lightboxIndex, setLightboxIndex] = useState(-1);
    const INITIAL_ITEMS = 24;
    const ITEMS_PER_PAGE = 24;
    const [visibleCount, setVisibleCount] = useState(INITIAL_ITEMS);
    const gridRef = useRef(null);
    const cols = config.columns || 3;
    const gridCls = cols === 2 ? 'md:grid-cols-2' : cols === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-3';
    const tp = s.dark;

    useEffect(() => {
        if (!gridRef.current) return;
        const ctx = gsap.context(() => {
            gsap.from(gridRef.current.children, {
                opacity: 0, scale: 0.9, duration: 0.4,
                stagger: 0.05, ease: 'power2.out',
                clearProps: 'all'
            });
        }, gridRef);
        return () => ctx.revert();
    }, [activeTab]);

    const variant = config.variant || 'grid';

    if (categories.length === 0) return (
        <section className={`${s.padClass} relative`} style={{backgroundColor: s.bg || '#ffffff'}}>
            <div className="max-w-4xl mx-auto px-4 py-16 text-center">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><rect x="2" y="2" width="20" height="20" rx="2" strokeWidth={1.5}/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2 15l5-5 3 3 4-4 8 8"/></svg>
                </div>
                <p className="text-gray-400 text-sm font-medium">Agrega categorias a la galeria</p>
                <p className="text-gray-300 text-xs mt-1">Configura las categorias en el editor</p>
            </div>
        </section>
    );

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    // Resolve images per category from mediaFolders
    const getCatImages = (cat) => {
        if (cat.folder_id && mediaFolders[cat.folder_id]) return mediaFolders[cat.folder_id];
        return cat.images || [];
    };

    // "Todos" tab shows all images
    const activeCat = categories[activeTab];
    const images = activeTab === 0 && activeCat?.name === 'Todos'
        ? categories.slice(1).flatMap(c => getCatImages(c))
        : getCatImages(activeCat);
    const filteredImages = images.filter(img => img.image_url);

    /* ── Shared tabs renderers ── */
    const renderPillTabs = () => categories.length > 1 && (
        <div className="flex flex-nowrap overflow-x-auto justify-center gap-2 mb-8 pb-1 -mx-1 px-1 scrollbar-hide">
            {categories.map((cat, i) => (
                <button key={i} onClick={() => { setActiveTab(i); setVisibleCount(INITIAL_ITEMS); }}
                    className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-all border ${i === activeTab ? 'text-white border-transparent shadow' : 'hover:opacity-80'}`}
                    style={i === activeTab
                        ? { backgroundColor: s.accent, borderColor: s.accent }
                        : { borderColor: tp ? 'rgba(255,255,255,0.25)' : '#d1d5db', color: s.textSub, backgroundColor: 'transparent' }}>
                    {cat.name}
                </button>
            ))}
        </div>
    );

    const renderDefaultTabs = () => categories.length > 1 && (
        <div className="flex flex-wrap justify-center gap-2 mb-8">
            {categories.map((cat, i) => (
                <button key={i} onClick={() => { setActiveTab(i); setVisibleCount(INITIAL_ITEMS); }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${i === activeTab ? 'text-white shadow-lg' : (tp ? 'bg-white/10 text-white/70 hover:bg-white/20' : 'hover:opacity-80')}`}
                    style={i === activeTab ? { backgroundColor: s.accent } : { backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6', color: s.textSub }}>
                    {cat.name}
                </button>
            ))}
        </div>
    );

    const renderLoadMore = () => visibleCount < filteredImages.length && (
        <div className="text-center mt-8">
            <button onClick={() => setVisibleCount(c => Math.min(c + ITEMS_PER_PAGE, filteredImages.length))}
                className="px-6 py-2.5 rounded-xl text-sm font-medium border-2 transition-all hover:shadow-md"
                style={{borderColor: s.accent, color: s.accent}}>
                Ver mas ({filteredImages.length - visibleCount} restantes)
            </button>
        </div>
    );

    const renderEmpty = () => images.length === 0 && (
        <p className="text-center text-sm py-12" style={{ color: s.textSub }}>Agrega imagenes desde la libreria de medios</p>
    );

    const renderLightbox = () => (
        <Lightbox
            open={lightboxIndex >= 0}
            close={() => setLightboxIndex(-1)}
            index={lightboxIndex}
            slides={lightboxIndex >= 0 ? filteredImages.map(img => ({ src: img.image_url, alt: img.title || '' })) : []}
        />
    );

    /* ── pills variant ── */
    if (variant === 'pills') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || 'Galeria'} subtitle={content.subtitle} config={config} s={s} />
                    {renderPillTabs()}
                    <div ref={gridRef} className={`grid grid-cols-2 ${gridCls} gap-3`}>
                        {filteredImages.slice(0, visibleCount).map((img, i) => (
                            <div key={`${activeTab}-${i}`} className={`group relative aspect-square overflow-hidden rounded-xl cursor-pointer transition-all duration-500 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
                                style={{ transitionDelay: visible ? `${Math.min(i, 9) * 60}ms` : '0ms' }}
                                onClick={() => setLightboxIndex(i)}>
                                <img src={img.image_url} alt={img.title || ''} srcSet={buildSrcSet(img)} sizes={getImageSizes(cols)} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" decoding="async" />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-end p-3">
                                    {img.title && <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity translate-y-2 group-hover:translate-y-0">{img.title}</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                    {renderLoadMore()}
                    {renderEmpty()}
                </div>
                {renderLightbox()}
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    /* ── masonry variant ── */
    if (variant === 'masonry') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || 'Galeria'} subtitle={content.subtitle} config={config} s={s} />
                    {renderDefaultTabs()}
                    <div ref={gridRef} className="columns-2 md:columns-3 gap-3" style={{ columnFill: 'balance' }}>
                        {filteredImages.slice(0, visibleCount).map((img, i) => (
                            <div key={`${activeTab}-${i}`}
                                className={`group relative overflow-hidden rounded-xl cursor-pointer mb-3 break-inside-avoid transition-all duration-500 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
                                style={{ transitionDelay: visible ? `${Math.min(i, 9) * 60}ms` : '0ms' }}
                                onClick={() => setLightboxIndex(i)}>
                                <img src={img.image_url} alt={img.title || ''} srcSet={buildSrcSet(img)} sizes={getImageSizes(cols)} className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-end p-3">
                                    {img.title && <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity translate-y-2 group-hover:translate-y-0">{img.title}</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                    {renderLoadMore()}
                    {renderEmpty()}
                </div>
                {renderLightbox()}
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    /* ── grid (default) ── */
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title || 'Galeria'} subtitle={content.subtitle} config={config} s={s} />
                {renderDefaultTabs()}
                {/* Grid */}
                <div ref={gridRef} className={`grid grid-cols-2 ${gridCls} gap-3`}>
                    {filteredImages.slice(0, visibleCount).map((img, i) => (
                        <div key={`${activeTab}-${i}`} className={`group relative aspect-square overflow-hidden rounded-xl cursor-pointer transition-all duration-500 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
                            style={{ transitionDelay: visible ? `${Math.min(i, 9) * 60}ms` : '0ms' }}
                            onClick={() => setLightboxIndex(i)}>
                            <img src={img.image_url} alt={img.title || ''} srcSet={buildSrcSet(img)} sizes={getImageSizes(cols)} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" decoding="async" />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-end p-3">
                                {img.title && <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity translate-y-2 group-hover:translate-y-0">{img.title}</span>}
                            </div>
                        </div>
                    ))}
                </div>
                {renderLoadMore()}
                {renderEmpty()}
            </div>
            {renderLightbox()}
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
